import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Check, X, Clock } from 'lucide-react';

const AttendanceCorrections = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [acting, setActing] = useState(false);

  const fetchAll = async () => {
    try {
      const res = await api.get('/attendance/corrections');
      setItems(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const decide = async (decision) => {
    if (!selected) return;
    setActing(true);
    try {
      await api.put(`/attendance/corrections/${selected.id}/decide`, { decision, remarks });
      toast.success(`Correction ${decision}d`);
      setSelected(null);
      setRemarks('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setActing(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const pending = items.filter(i => i.status === 'pending');
  const decided = items.filter(i => i.status !== 'pending');

  const renderTable = (rows) => (
    <Card><CardContent className="p-0">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Employee</TableHead><TableHead>Code</TableHead><TableHead>Date</TableHead>
          <TableHead>Requested In</TableHead><TableHead>Requested Out</TableHead>
          <TableHead>Status</TableHead><TableHead>Reason</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No requests</TableCell></TableRow>}
          {rows.map(r => (
            <TableRow key={r.id} data-testid={`correction-${r.id}`}>
              <TableCell className="font-medium">{r.employee_name}</TableCell>
              <TableCell>{r.employee_code}</TableCell>
              <TableCell>{r.date}</TableCell>
              <TableCell className="text-xs">{r.requested_punch_in ? new Date(r.requested_punch_in).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '—'}</TableCell>
              <TableCell className="text-xs">{r.requested_punch_out ? new Date(r.requested_punch_out).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '—'}</TableCell>
              <TableCell><Badge variant={r.status === 'approved' ? 'default' : r.status === 'rejected' ? 'destructive' : 'secondary'}>{r.status}</Badge></TableCell>
              <TableCell className="max-w-xs truncate">{r.reason}</TableCell>
              <TableCell>
                {r.status === 'pending' && (
                  <Button size="sm" variant="outline" onClick={() => { setSelected(r); setRemarks(''); }} data-testid={`review-correction-${r.id}`}>Review</Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent></Card>
  );

  return (
    <div className="space-y-6" data-testid="attendance-corrections-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Clock />Attendance Corrections</h1>
        <p className="text-muted-foreground">Review and decide employee attendance correction requests</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Pending</p><p className="text-2xl font-bold text-amber-600">{pending.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Approved</p><p className="text-2xl font-bold text-emerald-600">{items.filter(i => i.status === 'approved').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Rejected</p><p className="text-2xl font-bold text-red-600">{items.filter(i => i.status === 'rejected').length}</p></CardContent></Card>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="decided">Decided ({decided.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending">{renderTable(pending)}</TabsContent>
        <TabsContent value="decided">{renderTable(decided)}</TabsContent>
      </Tabs>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Review Correction Request</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div><span className="text-muted-foreground">Employee:</span> {selected.employee_name} ({selected.employee_code})</div>
              <div><span className="text-muted-foreground">Date:</span> {selected.date}</div>
              <div><span className="text-muted-foreground">Requested punch in:</span> {selected.requested_punch_in || '—'}</div>
              <div><span className="text-muted-foreground">Requested punch out:</span> {selected.requested_punch_out || '—'}</div>
              <div><span className="text-muted-foreground">Reason:</span> <span className="block bg-muted/30 p-2 rounded mt-1">{selected.reason}</span></div>
              <Textarea placeholder="HR remarks (optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="correction-remarks" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => decide('reject')} disabled={acting} data-testid="reject-correction-btn">
              {acting && <Loader2 size={14} className="animate-spin mr-1" />}<X size={14} className="mr-1" />Reject
            </Button>
            <Button onClick={() => decide('approve')} disabled={acting} data-testid="approve-correction-btn">
              {acting && <Loader2 size={14} className="animate-spin mr-1" />}<Check size={14} className="mr-1" />Approve & Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendanceCorrections;
